import {
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Table,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { DefaultOptionType } from "antd/es/cascader";
import type { ColumnType } from "antd/es/table";
import type { FilterFunc } from "rc-select/lib/Select";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { EditableText, editableTypes } from "../../../common/components/EditableText";
import { TASK_DESCRIPTION_MAX_LENGTH } from "../../../common/constants/constants";
import {
  FilePreviewModal,
  useFilePreview,
} from "../../../common/components/FilePreviewModal";
import { canDeleteFile } from "../../../common/utils/canDeleteFile";
import { useDeepLinkScroll } from "../../../common/utils/useDeepLinkScroll";
import { ticketActions } from "../../../features/ticket/ticketSlice";
import { ticketDataActions } from "../../../features/ticket-data/ticketDataSlice";
import { buildDownloadUrl } from "../../files/downloadUrl";
import { startViewUpload, updateFileRelation } from "../../files/store";
import { downloadAllTicketFiles } from "../../files/zip-export/store";
import type {
  Blade,
  FileRelationsDetailed,
  ModelFile,
  Services,
  TicketItem,
  TicketItemDetailed,
} from "../../generated-axios-client";
import type { RootState } from "../../store";
import { FileActionsCell, FileNameCell } from "./FilesInfo";
import styles from "./TasksInfo.module.css";
import type { TicketViewRenderProps } from "./TicketViewRender";

type TaskRow = {
  key: string;
  manufacturer: string;
  model: string;
  serial_number: string;
  set_number: string;
  service: string;
  description: string;
  estimated_time_spent: number | null;
  total_time: number | null;
  completed: boolean;
  originalItem: TicketItemDetailed;
};

type TaskModalState = {
  open: boolean;
  item: TicketItemDetailed | null;
};

type TaskFilter = "all" | "todo" | "completed";

const TaskExpandedRow = ({
  row,
  intl,
  taskFiles,
  ticketId,
  dispatch,
}: {
  row: TaskRow;
  intl: IntlShape;
  taskFiles: FileRelationsDetailed[];
  ticketId: string;
  dispatch: Dispatch;
}) => {
  const na = <Typography.Text type="secondary">—</Typography.Text>;
  const { previewFile, openPreview, closePreview } = useFilePreview();
  const messageApi = useSelector((state: RootState) => state.appFeedback.message);
  const currentUser = useSelector((state: RootState) => state.userstate.user);

  const fileColumns: ColumnType<FileRelationsDetailed>[] = [
    {
      key: "name",
      width: 240,
      render: (_: unknown, fr: FileRelationsDetailed) => (
        <FileNameCell file={fr.file as ModelFile} />
      ),
    },
    {
      key: "description",
      render: (_: unknown, fr: FileRelationsDetailed) => (
        <EditableText
          variableName="description"
          variableValue={fr.description ?? undefined}
          intl={intl}
          dataType={editableTypes.TEXT}
          onChange={
            fr.id
              ? (_name, value) =>
                  dispatch(
                    updateFileRelation({
                      relationId: fr.id as string,
                      description: value || null,
                    })
                  )
              : undefined
          }
          maxLength={255}
        />
      ),
    },
    {
      key: "actions",
      width: 116,
      render: (_: unknown, fr: FileRelationsDetailed) => (
        <FileActionsCell
          file={fr.file as ModelFile}
          canDelete={canDeleteFile({
            uploadedBy: fr.uploaded_by,
            currentUserId: currentUser?.id,
            currentUserGroups: currentUser?.groups,
          })}
          intl={intl}
          dispatch={dispatch}
          onPreview={openPreview}
        />
      ),
    },
  ];

  const detailItems = [
    {
      key: "description",
      label: intl.formatMessage({ id: "ticket_description" }),
      children: row.description || na,
    },
    {
      key: "manufacturer",
      label: intl.formatMessage({ id: "blade_table_column_manufacturer" }),
      children: row.manufacturer || na,
    },
    {
      key: "model",
      label: intl.formatMessage({ id: "blade_table_column_model" }),
      children: row.model || na,
    },
    {
      key: "set_number",
      label: intl.formatMessage({ id: "blade_table_column_set_number" }),
      children: row.set_number || na,
    },
  ];

  const timeItems = [
    {
      key: "estimated_time_spent",
      label: intl.formatMessage({ id: "ticket_task_estimated_time" }),
      children: row.estimated_time_spent != null ? `${row.estimated_time_spent}h` : na,
    },
    {
      key: "total_time",
      label: intl.formatMessage({ id: "ticket_task_actual_time" }),
      children: row.total_time != null ? `${row.total_time}h` : na,
    },
  ];

  return (
    <Flex
      vertical
      gap={16}
      style={{
        padding: "8px 32px 8px 48px",
        width: "100%",
      }}
    >
      <Flex gap={24} wrap>
        <Flex vertical style={{ flex: "1 1 320px", minWidth: 0 }}>
          <Typography.Text strong style={{ fontSize: 12, marginBottom: 8 }}>
            {intl.formatMessage({ id: "ticket_task_detail_section" })}
          </Typography.Text>
          <Descriptions
            items={detailItems}
            size="small"
            column={1}
            bordered={false}
            labelStyle={{ color: "var(--ant-color-text-secondary)", width: 100 }}
          />
        </Flex>
        <Flex vertical style={{ flex: "0 0 240px", minWidth: 0 }}>
          <Typography.Text strong style={{ fontSize: 12, marginBottom: 8 }}>
            {intl.formatMessage({ id: "ticket_task_time_section" })}
          </Typography.Text>
          <Descriptions
            items={timeItems}
            size="small"
            column={1}
            bordered={false}
            labelStyle={{ color: "var(--ant-color-text-secondary)", width: 100 }}
          />
        </Flex>
      </Flex>

      <Flex vertical gap={8} style={{ width: "100%" }}>
        <Flex align="center" justify="space-between">
          <Typography.Text strong style={{ fontSize: 12 }}>
            {intl.formatMessage({ id: "ticket_tab_files" })}
          </Typography.Text>
          <Flex gap={8} align="center">
            <Upload
              multiple
              showUploadList={false}
              customRequest={(options) => {
                if (options.file instanceof File) {
                  const hide = messageApi?.loading(
                    intl.formatMessage({ id: "upload_uploading" }),
                    0
                  );
                  dispatch(
                    startViewUpload({
                      file: options.file,
                      ticketId,
                      ticketItemId: row.originalItem.id,
                      onSuccess: () => {
                        hide?.();
                        messageApi?.success(intl.formatMessage({ id: "upload_success" }));
                      },
                      onError: () => {
                        hide?.();
                        messageApi?.error(intl.formatMessage({ id: "upload_failed" }));
                      },
                    })
                  );
                }
              }}
            >
              <Button type="dashed" size="small" icon={<UploadOutlined />}>
                {intl.formatMessage({ id: "ticket_task_upload_file" })}
              </Button>
            </Upload>
            {taskFiles.length > 0 && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  dispatch(
                    downloadAllTicketFiles({
                      files: taskFiles.map((fr) => fr.file as ModelFile),
                      taskNumber: row.originalItem.ticket_item_number ?? null,
                    })
                  )
                }
              >
                {intl.formatMessage({ id: "ticket_task_files_download_all" })}
              </Button>
            )}
          </Flex>
        </Flex>
        {taskFiles.length > 0 ? (
          <Table
            size="small"
            dataSource={taskFiles}
            rowKey={(fr) => fr.id ?? fr.file?.id ?? ""}
            pagination={false}
            showHeader={false}
            columns={fileColumns}
            style={{ width: "100%" }}
            tableLayout="fixed"
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {intl.formatMessage({ id: "ticket_task_files_empty" })}
          </Typography.Text>
        )}
      </Flex>

      <FilePreviewModal
        file={previewFile}
        onClose={closePreview}
        getDownloadUrl={buildDownloadUrl}
        intl={intl}
      />
    </Flex>
  );
};

function buildServiceOptions(serviceObjects: Record<string, Services>) {
  return Object.values(serviceObjects).map((s) => ({
    key: s.id,
    value: s.id,
    label: s.name,
  }));
}

function buildBladeOptions(blades: Blade[]) {
  return blades.map((b) => ({
    key: b.id,
    value: b.id,
    label: b.serial_number,
  }));
}

const searchHandler: FilterFunc<DefaultOptionType> = (input, option) => {
  const search = input.toLowerCase();
  const value = option?.label?.toString().toLowerCase?.() ?? "";
  return value.includes(search);
};

type TaskFormValues = {
  blade: string;
  service?: string | null;
  description?: string | null;
  estimated_time_spent?: number | null;
  completed?: boolean | null;
};

export const TasksInfo = (props: TicketViewRenderProps & { dispatch: Dispatch }) => {
  const { ticketData, intl, dispatch, currentRoute } = props;
  const { ticket, serviceObjects } = ticketData;

  const linkedTaskId = currentRoute?.params?.taskId as string | undefined;
  const linkedSection = currentRoute?.params?.section as string | undefined;

  const scrollToTasks =
    linkedSection === "tasks" || (linkedSection === "files" && Boolean(linkedTaskId));

  const [modal, setModal] = useState<TaskModalState>({ open: false, item: null });
  const [taskFilter, setTaskFilter] = useState<TaskFilter>("todo");
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly string[]>([]);
  const [form] = Form.useForm<TaskFormValues>();
  const tasksCardRef = useRef<HTMLDivElement>(null);

  useDeepLinkScroll(tasksCardRef, scrollToTasks ? `${linkedTaskId ?? "list"}` : null);

  const serviceOptions = useMemo(
    () => buildServiceOptions(serviceObjects),
    [serviceObjects]
  );

  const storedBlades = useSelector((state: RootState) => state.ticketData.blades);

  useEffect(() => {
    if (ticket.turbine?.id) {
      dispatch(ticketDataActions.fetchTicketData({ turbine: ticket.turbine.id }));
    }
  }, [ticket.turbine?.id, dispatch]);

  const taskFilesByItem = useMemo(() => {
    const grouped: Record<string, FileRelationsDetailed[]> = {};
    for (const fr of ticket.file_relations ?? []) {
      if (!fr.ticket_item) continue;
      if (!grouped[fr.ticket_item]) grouped[fr.ticket_item] = [];
      grouped[fr.ticket_item].push(fr);
    }
    return grouped;
  }, [ticket.file_relations]);

  const bladeOptions = useMemo(
    () => buildBladeOptions(storedBlades ?? []),
    [storedBlades]
  );

  const openAdd = () => {
    form.resetFields();
    setModal({ open: true, item: null });
  };

  const openEdit = (item: TicketItemDetailed) => {
    const parsedEstimate = Number(item.estimated_time_spent);
    form.setFieldsValue({
      blade: item.blade?.id,
      service: item.service ?? null,
      description: item.description ?? null,
      estimated_time_spent: Number.isNaN(parsedEstimate) ? undefined : parsedEstimate,
      completed: item.completed ?? false,
    });
    setModal({ open: true, item });
  };

  const buildBasePayload = (): TicketItem[] =>
    ticket.items.map(
      (i): TicketItem => ({
        id: i.id,
        ticket: ticket.id,
        blade: i.blade?.id ?? undefined,
        service: i.service ?? null,
        description: i.description ?? null,
        estimated_time_spent: i.estimated_time_spent ?? null,
        completed: i.completed ?? null,
      })
    );

  const handleSave = () => {
    form.validateFields().then((values) => {
      const existing = buildBasePayload();

      let updatedItems: TicketItem[];
      if (modal.item) {
        updatedItems = existing.map((i) =>
          i.id === modal.item!.id
            ? {
                ...i,
                blade: values.blade,
                service: values.service ?? null,
                description: values.description ?? null,
                estimated_time_spent:
                  values.estimated_time_spent != null
                    ? String(values.estimated_time_spent)
                    : null,
                completed: values.completed ?? null,
              }
            : i
        );
      } else {
        updatedItems = [
          ...existing,
          {
            ticket: ticket.id,
            blade: values.blade,
            service: values.service ?? null,
            description: values.description ?? null,
            estimated_time_spent:
              values.estimated_time_spent != null
                ? String(values.estimated_time_spent)
                : null,
            completed: values.completed ?? false,
          },
        ];
      }

      dispatch(
        ticketActions.saveTicketItems({ ticketId: ticket.id!, items: updatedItems })
      );
      setModal({ open: false, item: null });
    });
  };

  const handleDeleteTask = (taskId: string) => {
    const remainingItems: TicketItem[] = ticket.items
      .filter((i) => i.id !== taskId)
      .map(
        (i): TicketItem => ({
          id: i.id,
          ticket: ticket.id,
          blade: i.blade?.id ?? undefined,
          service: i.service ?? null,
          description: i.description ?? null,
          estimated_time_spent: i.estimated_time_spent ?? null,
          completed: i.completed ?? null,
        })
      );

    dispatch(
      ticketActions.saveTicketItems({ ticketId: ticket.id!, items: remainingItems })
    );
  };

  const columns: ColumnType<TaskRow>[] = [
    {
      key: "task_number",
      title: "#",
      width: 44,
      render: (_: unknown, row: TaskRow) => (
        <Typography.Text type="secondary">
          {row.originalItem.ticket_item_number != null
            ? `#${row.originalItem.ticket_item_number}`
            : "—"}
        </Typography.Text>
      ),
    },
    {
      key: "blade",
      title: intl.formatMessage({ id: "ticket_task_blade" }),
      render: (_: unknown, row: TaskRow) => {
        const secondaryParts = [
          row.manufacturer,
          row.model,
          row.set_number ? `Set ${row.set_number}` : null,
        ].filter(Boolean);
        return (
          <Flex vertical gap={2}>
            <Typography.Text strong ellipsis>
              {row.serial_number || "—"}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {secondaryParts.length > 0 ? secondaryParts.join(" · ") : "—"}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      key: "task",
      title: intl.formatMessage({ id: "ticket_task_column" }),
      render: (_: unknown, row: TaskRow) => (
        <Flex vertical gap={2}>
          <Typography.Text strong ellipsis>
            {row.service || "—"}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {row.description || "—"}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      key: "time",
      title: intl.formatMessage({ id: "ticket_task_time_column" }),
      width: 120,
      render: (_: unknown, row: TaskRow) => {
        const estimated = row.estimated_time_spent;
        const spent = row.total_time;
        const isOver = estimated != null && spent != null && spent > estimated;
        return (
          <Flex vertical gap={2}>
            <Typography.Text ellipsis>
              {estimated != null ? `${estimated}h` : "—"}
            </Typography.Text>
            <Typography.Text
              type={spent != null ? (isOver ? "danger" : "secondary") : "secondary"}
              style={{ fontSize: 12 }}
              ellipsis
            >
              {spent != null
                ? `${spent}h`
                : intl.formatMessage({ id: "ticket_task_no_time_logged" })}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      key: "completed",
      title: intl.formatMessage({ id: "ticket_task_completed" }),
      dataIndex: "completed",
      width: 70,
      align: "center" as const,
      render: (val: boolean) => (
        <CheckCircleOutlined
          style={{
            fontSize: 18,
            color: val ? "var(--ant-color-success)" : "var(--ant-color-border)",
          }}
        />
      ),
    },
    {
      key: "actions",
      width: 84,
      render: (_: unknown, row: TaskRow) => (
        <Flex>
          <Tooltip title={intl.formatMessage({ id: "ticket_task_edit_title" })}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row.originalItem)}
            />
          </Tooltip>
          <Popconfirm
            title={intl.formatMessage({ id: "ticket_task_delete_title" })}
            description={intl.formatMessage({ id: "ticket_task_delete_confirm" })}
            okText={intl.formatMessage({ id: "confirm" })}
            okType="danger"
            cancelText={intl.formatMessage({ id: "cancel" })}
            onConfirm={() => handleDeleteTask(row.originalItem.id!)}
          >
            <Tooltip title={intl.formatMessage({ id: "ticket_task_delete_title" })}>
              <Button type="text" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  const rows: TaskRow[] = ticket.items.map((item) => {
    const parsedEstimate = Number(item.estimated_time_spent);
    return {
      key: item.id ?? "",
      manufacturer: item.blade?.manufacturer ?? "",
      model: item.blade?.model ?? "",
      serial_number: item.blade?.serial_number ?? "",
      set_number: item.blade?.set_number ?? "",
      service: item.service ? (serviceObjects[item.service]?.name ?? item.service) : "",
      description: item.description ?? "",
      estimated_time_spent: Number.isNaN(parsedEstimate) ? null : parsedEstimate,
      total_time: item.total_time ?? null,
      completed: item.completed ?? false,
      originalItem: item,
    };
  });

  const filteredRows = useMemo(
    () =>
      taskFilter === "all"
        ? rows
        : rows.filter((row) =>
            taskFilter === "completed" ? row.completed === true : row.completed !== true
          ),
    [rows, taskFilter]
  );

  const linkedTaskIsHidden =
    linkedTaskId !== undefined &&
    !filteredRows.some((row) => row.key === linkedTaskId) &&
    rows.some((row) => row.key === linkedTaskId);

  useEffect(() => {
    if (!linkedTaskId) return;
    setExpandedRowKeys([linkedTaskId]);
  }, [linkedTaskId]);

  useEffect(() => {
    if (linkedTaskIsHidden) setTaskFilter("all");
  }, [linkedTaskIsHidden]);

  const todoCount = rows.filter((row) => row.completed !== true).length;
  const completedCount = rows.filter((row) => row.completed === true).length;

  const filterOptions = [
    {
      label: `${intl.formatMessage({ id: "ticket_task_filter_all" })} (${rows.length})`,
      value: "all",
    },
    {
      label: `${intl.formatMessage({ id: "ticket_task_filter_todo" })} (${todoCount})`,
      value: "todo",
    },
    {
      label: `${intl.formatMessage({ id: "ticket_task_filter_completed" })} (${completedCount})`,
      value: "completed",
    },
  ];

  return (
    <>
      <Card
        ref={tasksCardRef}
        title={intl.formatMessage({ id: "ticket_tasks" })}
        extra={
          <Flex gap={8} align="center">
            <Segmented
              options={filterOptions}
              value={taskFilter}
              onChange={(value) => setTaskFilter(value as TaskFilter)}
              size="small"
            />
            <Button icon={<PlusOutlined />} size="small" onClick={openAdd}>
              {intl.formatMessage({ id: "ticket_button_add_task" })}
            </Button>
          </Flex>
        }
      >
        <Table
          className={styles.tasksTable}
          columns={columns}
          dataSource={filteredRows}
          rowKey="key"
          pagination={false}
          size="small"
          tableLayout="fixed"
          rowClassName={(row: TaskRow) =>
            row.key === linkedTaskId ? styles.linkedTaskRow : ""
          }
          expandable={{
            expandedRowKeys: [...expandedRowKeys],
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
            expandedRowRender: (row: TaskRow) => (
              <TaskExpandedRow
                row={row}
                intl={intl}
                taskFiles={
                  row.originalItem.id ? (taskFilesByItem[row.originalItem.id] ?? []) : []
                }
                ticketId={ticket.id!}
                dispatch={dispatch}
              />
            ),
          }}
        />
      </Card>

      <Modal
        open={modal.open}
        title={intl.formatMessage({
          id: modal.item ? "ticket_task_edit_title" : "ticket_task_add_title",
        })}
        okText={intl.formatMessage({ id: "confirm" })}
        cancelText={intl.formatMessage({ id: "cancel" })}
        onOk={handleSave}
        onCancel={() => setModal({ open: false, item: null })}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="blade"
            label={intl.formatMessage({ id: "ticket_task_blade" })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "validation_message_ticket_task_blade",
                }),
              },
            ]}
          >
            <Select
              options={bladeOptions}
              showSearch
              filterOption={searchHandler}
              placeholder={intl.formatMessage({ id: "ticket_blade_select_placeholder" })}
            />
          </Form.Item>
          <Form.Item
            name="service"
            label={intl.formatMessage({ id: "ticket_task_service" })}
          >
            <Select
              options={serviceOptions}
              showSearch
              filterOption={searchHandler}
              allowClear
              placeholder={intl.formatMessage({ id: "ticket_task_service" })}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={intl.formatMessage({ id: "ticket_description" })}
            rules={[
              {
                max: TASK_DESCRIPTION_MAX_LENGTH,
                message: intl.formatMessage(
                  { id: "validation_message_max_length" },
                  { max: TASK_DESCRIPTION_MAX_LENGTH }
                ),
              },
            ]}
          >
            <Input.TextArea
              rows={3}
              maxLength={TASK_DESCRIPTION_MAX_LENGTH}
              showCount
            />
          </Form.Item>
          <Flex gap="middle" align="flex-end">
            <Form.Item
              name="estimated_time_spent"
              label={intl.formatMessage({ id: "ticket_task_estimated_time" })}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                step={0.5}
                precision={1}
                addonAfter="h"
                placeholder="e.g. 2.5"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name="completed"
              valuePropName="checked"
              label={intl.formatMessage({ id: "ticket_task_completed" })}
              style={{ marginBottom: 0 }}
            >
              <Checkbox />
            </Form.Item>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
